import {
  Commute,
  ContentCopy as ContentCopyIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import clsx from "clsx";
import { FC, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { AccessControl, MuiButton } from "@ryde/controls";
import {
  useCourseEdit,
  useCrudEditContext,
  useCrudNavigate,
  useDynamicFormsFetch,
  useModalConfirm,
  useRideDebugActions,
} from "@ryde/redux";
import {
  canResolveAlways,
  ConfirmResult,
  createSuccessResult,
  EditFormMode,
  ICourseDto,
  IExtendableDto,
  isInnerItemEditingAtom,
  loadersEditFormDataSF,
  Log,
  TCanResolve,
  TDrawerCloseFunction,
  TResolveFn,
  useDebugMode,
  warnAndToastSimpleResult,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import { useEditFormInner } from "../../_core";
import { EditFormFooterButton } from "../../../right";
import {
  CourseFooterLeftButtonsStyled,
  CourseFooterRightButtonsStyled,
  CourseFooterStyled,
} from "./course.footer.styled";
import { useCourseProduceByDateRange } from "./use-course-produce-by-date-range";

interface ICourseFooter {
  isCourseDirty?: boolean;
  onCloseDrawer: TDrawerCloseFunction;
  resolveFn?: TResolveFn;
  canResolve?: TCanResolve;
  hasPrefilledDraft?: boolean;
}

export const CourseFooter: FC<ICourseFooter> = ({
  isCourseDirty,
  resolveFn,
  onCloseDrawer,
  canResolve = canResolveAlways,
  hasPrefilledDraft,
}) => {
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);
  const { t } = useTranslation();
  const {
    entity,
    mode,
    data: { dto: courseDto, isEdit },
  } = useCrudEditContext<ICourseDto>();

  const { navigateToList } = useCrudNavigate("course");
  const { fetchTableRows } = useDynamicFormsFetch("course");
  const { saveCourse, cleanupCourseAtoms } = useCourseEdit({
    hasPrefilledDraft,
  });
  const courseId = courseDto?.id;
  const loading = useRecoilValue(loadersEditFormDataSF(entity));
  const { confirmDialogResult } = useModalConfirm();
  const { onDuplicateClick, onArchiveClick, closeSidebar } = useEditFormInner({
    onCloseDrawer,
    resolveFn,
    canResolve,
  });
  const { hasDebugModeCourseQueryParam } = useDebugMode();
  const { createRideByCourse } = useRideDebugActions();
  const { openProduceByDateRange } = useCourseProduceByDateRange();

  const onSaveClick: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ): Promise<void> => {
    e.preventDefault();

    try {
      if (!courseDto) {
        Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.INVALID_DATA`));
        return;
      }

      const courseSaveResult = await saveCourse(courseDto);

      if (courseSaveResult.success) {
        cleanupCourseAtoms();
        // Hands the saved course back to whoever opened the drawer (e.g. bulk create from the stations page).
        resolveFn?.(
          createSuccessResult(courseSaveResult.data as IExtendableDto),
        );

        if (mode !== EditFormMode.CrudRoute) {
          onCloseDrawer();
        } else {
          navigateToList();
          fetchTableRows();
        }
      } else {
        warnAndToastSimpleResult(courseSaveResult, t);
      }
    } catch (error) {
      Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.SAVING_ERROR`), error);
    }
  };

  const onDuplicateClickEvent: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ): Promise<void> => {
    e.preventDefault();
    const confirmationResult = await confirmDialogResult({
      title: t("general.DUPLICATE_CONFIRM"),
      message: t(`general.ITEM_DUPLICATE_MESSAGE`),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    cleanupCourseAtoms();
    onDuplicateClick();
  };

  const onArchiveClickEvent: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ): Promise<void> => {
    e.preventDefault();
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.ITEM_DELETE_MESSAGE"),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    onArchiveClick();
  };

  const onCreateRideByCourseClick: MouseEventHandler<
    HTMLButtonElement
  > = async (e): Promise<void> => {
    e.preventDefault();

    if (!courseDto?.id) {
      return;
    }

    createRideByCourse(courseDto);
  };

  const onProduceByDateRangeClick: MouseEventHandler<
    HTMLButtonElement
  > = async (e): Promise<void> => {
    e.preventDefault();

    if (!courseDto?.id) {
      return;
    }

    await openProduceByDateRange(courseDto);
  };

  return (
    <CourseFooterStyled
      className={clsx(
        "CourseDrawer-Footer",
        isInnerItemEditing && "inner-item-editing",
      )}
    >
      <CourseFooterLeftButtonsStyled>
        <AccessControl entity="course" action="delete">
          {!!courseId && !!isEdit && (
            <EditFormFooterButton
              onClick={onArchiveClickEvent}
              disabled={loading}
            >
              <InventoryIcon style={{ fontSize: "16px" }} />
              <span>{t(`general.ARCHIVE`)}</span>
            </EditFormFooterButton>
          )}
        </AccessControl>

        <AccessControl entity="course" action="create">
          {!!courseId && !!isEdit && (
            <EditFormFooterButton
              onClick={onDuplicateClickEvent}
              disabled={loading}
            >
              <ContentCopyIcon style={{ fontSize: "16px" }} />
              <span>{t(`general.DUPLICATE`)}</span>
            </EditFormFooterButton>
          )}
        </AccessControl>

        <AccessControl entity="booking" action="produce">
          {!!courseId && !!isEdit && (
            <EditFormFooterButton
              onClick={onProduceByDateRangeClick}
              disabled={loading}
            >
              <SVG_MUI.ProductionSVG color={greyMui._700} />
              <span>{t(`booking.bulk.produce`)}</span>
            </EditFormFooterButton>
          )}
        </AccessControl>

        <AccessControl entity="ride" action="create">
          {!!courseId && hasDebugModeCourseQueryParam && (
            <EditFormFooterButton
              onClick={onCreateRideByCourseClick}
              disabled={loading}
            >
              <Commute style={{ fontSize: "16px" }} />
              <span>{t(`course.CREATE_RIDE_FROM_COURSE`)}</span>
            </EditFormFooterButton>
          )}
        </AccessControl>
      </CourseFooterLeftButtonsStyled>
      <CourseFooterRightButtonsStyled>
        <MuiButton
          muiVariant={"outlined"}
          color={"secondary"}
          onClick={closeSidebar}
          loading={loading}
        >
          {t(`general.CANCEL`)}
        </MuiButton>

        <MuiButton
          loading={loading}
          disabled={isInnerItemEditing || loading || !isCourseDirty}
          onClick={onSaveClick}
          width={140}
          muiStyle={"secondary"}
        >
          {t(`general.SAVE`)}
        </MuiButton>
      </CourseFooterRightButtonsStyled>
    </CourseFooterStyled>
  );
};
